/**
 * Shared dashboard API client.
 *
 * Responsibilities:
 *   • Resolve the API base URL (env override → same-origin `/api`).
 *   • Attach the Clerk-issued platform JWT as `Authorization: Bearer`.
 *   • Attach the `X-Spotlight-Tenant-Id` header on tenant-scoped
 *     calls.  The active tenant lives in localStorage so the
 *     selection survives full-page refreshes.
 *   • Unwrap the envelope + error responses into something callers
 *     can `await` without `resp.ok` / `resp.status` scaffolding in
 *     every route.
 *
 * Every dashboard fetch goes through this — NEVER send `X-API-Key`
 * from the dashboard.  The API key identifies a tenant on the SDK
 * edge; dashboard identity is platform-user + membership.
 */

const TENANT_STORAGE_KEY = 'spotlight.active_tenant';
// Plaintext API keys the user has minted in this browser.  Stored
// client-side so the Bookmarklet page can offer "use this key" and
// actually hand back the secret — the backend only keeps the hash,
// so without this cache the user would have to re-paste the full
// key from their own notes every time.  Keyed by prefix, cleared
// on revoke + user-initiated Forget.
//
// Lives in sessionStorage (not localStorage) so the cache dies with
// the tab — an XSS that lands while the dashboard is closed can't
// recover keys minted in a previous session.  Trade-off: opening a
// new tab loses the "use this key" affordance until the user mints
// or pastes again, which is the right side of the security/UX line
// for a secret-bearing cache.
const API_KEY_PLAINTEXT_STORAGE_KEY = 'spotlight.api_key_plaintexts';

/**
 * Remember a freshly-minted API key in the browser so other pages
 * on the same origin can offer it back to the user.  Scoped by
 * tenant — switching tenant stops exposing the old tenant's keys.
 */
export function rememberApiKey(prefix, fullKey, { tenantId = currentTenantId() } = {}) {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  if (!prefix || !fullKey) return;
  const all = readKeyCache();
  all[tenantId] = { ...(all[tenantId] || {}), [prefix]: fullKey };
  window.sessionStorage.setItem(API_KEY_PLAINTEXT_STORAGE_KEY, JSON.stringify(all));
}

/**
 * Look up the plaintext for a remembered key prefix, if any.
 * Returns null if the user never opted to remember it, or if the
 * prefix doesn't match what we have.
 */
export function recallApiKey(prefix, { tenantId = currentTenantId() } = {}) {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  const all = readKeyCache();
  return (all[tenantId] || {})[prefix] || null;
}

/**
 * Drop a remembered plaintext (one prefix, or every key on the
 * current tenant if prefix is omitted).  Called from the
 * Bookmarklet page's "Forget" button and on Revoke.
 */
export function forgetApiKey(prefix, { tenantId = currentTenantId() } = {}) {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  const all = readKeyCache();
  if (!all[tenantId]) return;
  if (prefix) {
    delete all[tenantId][prefix];
  } else {
    delete all[tenantId];
  }
  window.sessionStorage.setItem(API_KEY_PLAINTEXT_STORAGE_KEY, JSON.stringify(all));
}

function readKeyCache() {
  try {
    return JSON.parse(window.sessionStorage.getItem(API_KEY_PLAINTEXT_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

// Every tenant_id is a v4 UUID.  The well-known demo tenant is
// seeded with `11111111-…-111111111111` so a fresh install lands
// somewhere the seed data actually exists without needing to know
// what UUID the install happens to have minted.  The all-zeroes
// UUID is reserved for "Spotlight itself" — the platform operator's
// dogfood tenant.
const DEMO_TENANT_UUID = '11111111-1111-1111-1111-111111111111';
const ENV_DEFAULT_TENANT_ID =
  import.meta.env.VITE_SPOTLIGHT_TENANT_ID || DEMO_TENANT_UUID;

/**
 * Default base URL for the Spotlight API.
 *
 * In production the dashboard lives at `{host}/dashboard` and the
 * API lives at `{host}/api` under the same host, so an empty env
 * var produces a correct same-origin URL without configuration.
 *
 * Fallback chain: explicit env → same-origin `/api` → localhost.
 */
export function resolveApiBase() {
  const explicit = import.meta.env.VITE_API_BASE;
  if (explicit) return explicit.replace(/\/+$/, '');
  if (typeof window !== 'undefined' && window.location) {
    return `${window.location.origin}/api`;
  }
  return 'http://localhost:8000';
}

/**
 * Default base URL for the SDK CDN.
 *
 * Returns a base that includes any necessary path prefix, so
 * callers just append `/manifest.json` or `/latest/spotlight.min.js`
 * directly — no need to add `/sdk/` themselves.
 *
 * Two layouts in production use:
 *   - Cloud:        `VITE_SDK_BASE=https://sdk.spotlight.help`
 *                   → manifest at `<base>/manifest.json`
 *   - Traefik dev:  no env var; derives `<host>/sdk`
 *                   → manifest at `<base>/manifest.json` == `<host>/sdk/manifest.json`
 *
 * Resolution: explicit `VITE_SDK_BASE` → derive `<api-host>/sdk` from API base.
 */
export function resolveSdkBase() {
  const explicit = import.meta.env.VITE_SDK_BASE;
  if (explicit) return explicit.replace(/\/+$/, '');
  const apiBase = resolveApiBase();
  const apiHost = apiBase.replace(/\/+api\/*$/, '').replace(/\/+$/, '');
  return `${apiHost}/sdk`;
}

/**
 * Return the tenant id this dashboard session is acting on.
 *
 * Resolution order:
 *   1. localStorage (set by the tenant switcher).
 *   2. VITE_SPOTLIGHT_TENANT_ID env var (build-time default).
 *   3. `demo_tenant` (seed default).
 */
export function currentTenantId() {
  if (typeof window !== 'undefined' && window.localStorage) {
    const stored = window.localStorage.getItem(TENANT_STORAGE_KEY);
    if (stored) return stored;
  }
  return ENV_DEFAULT_TENANT_ID;
}

/**
 * Persist the active tenant and reload.
 *
 * We reload rather than propagate via React state because every
 * open page has already baked the tenant id into its fetch loop;
 * a full reload is the cleanest way to re-scope everything at
 * once without elaborate cross-component invalidation.
 */
export function setActiveTenantId(tenantId) {
  if (typeof window !== 'undefined' && window.localStorage) {
    if (tenantId) {
      window.localStorage.setItem(TENANT_STORAGE_KEY, tenantId);
    } else {
      window.localStorage.removeItem(TENANT_STORAGE_KEY);
    }
    // Hard reload to reset all in-memory query caches.  The base
    // path differs by deploy (docker-compose: /dashboard/ behind
    // Traefik; Cloudflare Pages: apex /) so we mirror the
    // BrowserRouter basename driven by VITE_BASE_PATH.
    const base = ((import.meta.env.VITE_BASE_PATH || '/dashboard/').replace(/\/$/, '') || '') + '/';
    window.location.assign(base);
  }
}

/**
 * Build a fetch caller bound to the current Clerk session.
 *
 * `projectId` adds the `X-Spotlight-Project` header; omit it on
 * tenant-wide pages so the backend falls back to the tenant's
 * default project.
 *
 * Usage:
 *
 *     const { getToken } = useAuth();
 *     const api = createApiClient(getToken);
 *     const projects = await api('/v1/admin/projects');
 */
export function createApiClient(getToken, { tenantId = currentTenantId(), projectId = null } = {}) {
  const base = resolveApiBase();
  const dashboardVersion = _resolveBuildVersion();

  return async function call(path, init = {}) {
    const token = await getToken();
    // Dev-mode bypass: when no real Clerk token is available, fall
    // back to the backend's X-Spotlight-Demo-Mode header so admin
    // endpoints still authorise the request.  Production builds
    // always have a token here, so the headers are never sent.
    const devHeaders = token
      ? {}
      : {
          'X-Spotlight-Demo-Mode': 'admin',
          'X-Spotlight-Demo-User': 'dev-user',
        };
    const resp = await fetch(`${base}${path}`, {
      ...init,
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'X-Spotlight-Tenant-Id': tenantId,
        ...devHeaders,
        ...(projectId ? { 'X-Spotlight-Project': projectId } : {}),
        // Stamp the dashboard's build version on every admin call,
        // mirroring the SDK's X-Spotlight-Bundle-Version /
        // X-Spotlight-SDK-Version headers.  CloudWatch can now
        // correlate "user X hit issue Y on dashboard build Z" the
        // same way it already does for customer SDK traffic — no
        // special treatment for first-party callers.
        ...(dashboardVersion ? { 'X-Spotlight-Dashboard-Version': dashboardVersion } : {}),
        'Content-Type': 'application/json',
        ...(init.headers || {}),
      },
    });
    if (!resp.ok) {
      throw new ApiError(resp.status, await _friendlyError(resp));
    }
    if (resp.status === 204) return null;
    const body = await resp.json();
    return body && 'data' in body ? body.data : body;
  };
}

/**
 * Build the standard auth headers for a platform-edge call.
 *
 * Production hits this with a real Clerk JWT; the dev-mode bypass
 * (`VITE_DEV_AUTH_BYPASS=true`) makes ``getToken()`` return ``''``
 * and we fall back to the backend's ``X-Spotlight-Demo-Mode``
 * header instead.  Centralising it here so the platform-tenant
 * endpoints behave the same as ``createApiClient`` calls — without
 * this, the dev bypass only worked for tenant-scoped admin
 * routes and `/v1/platform/*` returned 401 / 422.
 */
function platformHeaders(token) {
  const dashboardVersion = _resolveBuildVersion();
  const baseHeaders = {
    'Content-Type': 'application/json',
    ...(dashboardVersion ? { 'X-Spotlight-Dashboard-Version': dashboardVersion } : {}),
  };
  if (token) {
    return {
      Authorization: `Bearer ${token}`,
      ...baseHeaders,
    };
  }
  return {
    Authorization: '',
    'X-Spotlight-Demo-Mode': 'admin',
    'X-Spotlight-Demo-User': 'dev-user',
    ...baseHeaders,
  };
}

/**
 * Resolve the dashboard build version once at module load.  Reads
 * the same `__SPOTLIGHT_BUILD_VERSION__` Vite define that
 * `build-info.js` consumes — keeping a single source of truth so
 * the header and the UserMenu footer never drift.
 */
function _resolveBuildVersion() {
  // eslint-disable-next-line no-undef
  if (typeof __SPOTLIGHT_BUILD_VERSION__ === 'string' && __SPOTLIGHT_BUILD_VERSION__) {
    // eslint-disable-next-line no-undef
    return __SPOTLIGHT_BUILD_VERSION__;
  }
  return '';
}

/**
 * Card 042 — reveal the plaintext of an own pk_live_* key.
 *
 * pk keys are publishable, so we KMS-wrap their plaintext at mint
 * and let the owner show it again on demand ("I lost my notes").
 * The backend enforces ownership + kind == "pk"; sk + mcp rows
 * return 403 with a clear "mint a new one" detail.
 *
 * Wrapper around the createApiClient call so route components
 * don't have to remember the POST verb + path shape.
 */
export async function revealApiKey(api, prefix) {
  const resp = await api(
    `/v1/admin/api-keys/${encodeURIComponent(prefix)}/reveal`,
    { method: 'POST', body: '{}' },
  );
  return resp?.key || '';
}

/**
 * Card 042 — toggle a key between active and disabled.
 *
 * Reuses the existing PATCH /v1/admin/api-keys/{prefix} endpoint
 * (which already accepts an ``active`` boolean).  Disabling a
 * key flips ``active=false`` — the row stays + can be re-enabled,
 * but the validator 401s on use just like a revoked row.  This
 * is the half-step between "still using it" and "soft-delete via
 * the Revoke button" — useful when you suspect a key is leaked
 * and want to break it temporarily while you investigate.
 */
export async function setApiKeyActive(api, prefix, active) {
  return api(
    `/v1/admin/api-keys/${encodeURIComponent(prefix)}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ active: Boolean(active) }),
    },
  );
}

/**
 * Owner-only rename of a tenant.  Other fields come later.
 */
export async function renameTenant(getToken, tenantId, name) {
  const token = await getToken();
  const base = resolveApiBase();
  const resp = await fetch(`${base}/v1/platform/tenants/${encodeURIComponent(tenantId)}`, {
    method: 'PATCH',
    headers: platformHeaders(token),
    body: JSON.stringify({ name }),
  });
  if (!resp.ok) {
    throw new ApiError(resp.status, await _friendlyError(resp));
  }
  const body = await resp.json();
  return body && 'data' in body ? body.data : body;
}

/**
 * Super-admin tenant create.  Only works when the signed-in
 * platform user's Clerk sub is in PLATFORM_SUPER_ADMIN_IDS; the
 * backend returns 403 otherwise.
 *
 * Backend takes ``{ name, slug }`` — `tenant_id` is minted
 * server-side so the client must NOT supply one.  Slug is the
 * URL-safe handle the dashboard surfaces; name is the display
 * string.
 */
export async function createTenant(getToken, { name, slug }) {
  const token = await getToken();
  const base = resolveApiBase();
  const resp = await fetch(`${base}/v1/platform/tenants`, {
    method: 'POST',
    headers: platformHeaders(token),
    body: JSON.stringify({ name, slug }),
  });
  if (!resp.ok) {
    throw new ApiError(resp.status, await _friendlyError(resp));
  }
  const body = await resp.json();
  const data = body && 'data' in body ? body.data : body;
  return data?.tenant;
}

/**
 * Call /v1/platform/tenants — the "which tenants can I reach"
 * endpoint.  Runs without a tenant header so it works before a
 * tenant has been picked.
 */
export async function fetchMyTenants(getToken) {
  const token = await getToken();
  const base = resolveApiBase();
  const resp = await fetch(`${base}/v1/platform/tenants`, {
    headers: platformHeaders(token),
  });
  if (!resp.ok) {
    throw new ApiError(resp.status, await _friendlyError(resp));
  }
  const body = await resp.json();
  const data = body && 'data' in body ? body.data : body;
  return {
    tenants: data?.tenants || [],
    isSuperAdmin: !!data?.is_super_admin,
  };
}

/**
 * GET /v1/platform/profile/me — the calling user's own profile.
 *
 * Runs without a tenant header because the profile is platform-wide
 * (same display name + avatar across every tenant the user belongs
 * to).  The same pattern as the other ``/v1/platform/*`` calls.
 */
export async function fetchMyProfile(getToken) {
  const token = await getToken();
  const base = resolveApiBase();
  const resp = await fetch(`${base}/v1/platform/profile/me`, {
    headers: platformHeaders(token),
  });
  if (!resp.ok) {
    throw new ApiError(resp.status, await _friendlyError(resp));
  }
  const body = await resp.json();
  return body && 'data' in body ? body.data : body;
}

/**
 * PATCH /v1/platform/profile/me — partial update of the caller's
 * own profile.  Fields left undefined are not touched server-side;
 * setting a field to ``""`` clears it (and the GET endpoint will
 * then fall back to the identity-provider value).
 */
export async function updateMyProfile(getToken, patch) {
  const token = await getToken();
  const base = resolveApiBase();
  const resp = await fetch(`${base}/v1/platform/profile/me`, {
    method: 'PATCH',
    headers: platformHeaders(token),
    body: JSON.stringify(patch),
  });
  if (!resp.ok) {
    throw new ApiError(resp.status, await _friendlyError(resp));
  }
  const body = await resp.json();
  return body && 'data' in body ? body.data : body;
}

/**
 * POST /v1/platform/profile/me/avatar — multipart upload of a new
 * avatar.  The backend enforces image-only (no SVG), 2 MB cap, and
 * a magic-byte check so renamed binaries can't slip through.
 *
 * NOTE: ``platformHeaders`` injects ``Content-Type: application/json``
 * which the multipart upload must NOT carry; we drop it explicitly
 * here so the browser sets the multipart boundary itself.
 */
export async function uploadAvatar(getToken, file) {
  const token = await getToken();
  const base = resolveApiBase();
  const form = new FormData();
  form.append('file', file);
  const headers = platformHeaders(token);
  delete headers['Content-Type'];
  const resp = await fetch(`${base}/v1/platform/profile/me/avatar`, {
    method: 'POST',
    headers,
    body: form,
  });
  if (!resp.ok) {
    throw new ApiError(resp.status, await _friendlyError(resp));
  }
  const body = await resp.json();
  return body && 'data' in body ? body.data : body;
}

/**
 * Build a renderable <img src> for a persisted avatar_url.
 *
 * The avatar lives in the help-attachments bucket which isn't public,
 * so the dashboard renders it through the same signed-URL streamer
 * that screenshots use.  For the current iteration the server stores
 * the S3 key on the profile row and the dashboard fetches the bytes
 * via the existing ``/v1/sdk/help/attachments/{id}`` route — but the
 * row's ``avatar_url`` is a key, not an id, so we route it through a
 * dedicated proxy path the backend can swap in later without a
 * client rebuild.  For now, when the value already looks like a URL
 * (``http`` / ``/v1/``) we pass it through verbatim, which is enough
 * to keep the UI rendering against IdP-hydrated avatars.
 */
export function signedAvatarSrc(avatarUrl) {
  if (!avatarUrl) return '';
  if (/^https?:\/\//.test(avatarUrl) || avatarUrl.startsWith('/v1/')) {
    return avatarUrl;
  }
  // S3 key — point at the dedicated avatar proxy.  The route is
  // implemented in a follow-up so the dashboard fallback is graceful:
  // until then it'll 404 quietly and the placeholder initial shows.
  const base = resolveApiBase();
  return `${base}/v1/platform/profile/avatars/${encodeURIComponent(avatarUrl)}`;
}

// ── Desktop push notifications (card spotlight-047) ──────────

/**
 * GET /v1/platform/push/public-key — VAPID public key, unauthenticated.
 *
 * Returned as a url-safe base64 string (no ``=`` padding) ready to
 * pass to ``pushManager.subscribe`` via the helper below.
 */
export async function fetchVapidPublicKey() {
  const base = resolveApiBase();
  const resp = await fetch(`${base}/v1/platform/push/public-key`);
  if (!resp.ok) {
    throw new ApiError(resp.status, await _friendlyError(resp));
  }
  const body = await resp.json();
  const payload = body && 'data' in body ? body.data : body;
  return payload.public_key;
}

/**
 * Convert the VAPID public key (url-safe base64) into the
 * ``Uint8Array`` the Web Push API wants.  The spec is annoyingly
 * specific — base64 with ``-/_`` and missing ``=`` padding — so
 * this helper lives close to the caller to avoid every consumer
 * re-implementing it.
 */
export function vapidKeyToUint8Array(b64) {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

/** POST /v1/admin/push/subscribe */
export async function operatorSubscribePush(api, subscription) {
  return api('/v1/admin/push/subscribe', {
    method: 'POST',
    body: JSON.stringify(subscription),
  });
}

/** DELETE /v1/admin/push/subscribe/{id} */
export async function operatorUnsubscribePush(api, subscriptionId) {
  return api(`/v1/admin/push/subscribe/${encodeURIComponent(subscriptionId)}`, {
    method: 'DELETE',
  });
}

/** GET /v1/admin/push/subscriptions — caller's own devices. */
export async function listMyPushSubscriptions(api) {
  const resp = await api('/v1/admin/push/subscriptions');
  return resp?.subscriptions || [];
}

/** PATCH /v1/admin/push/subscribe/{id}/dnd */
export async function setPushDoNotDisturb(api, subscriptionId, dnd) {
  return api(`/v1/admin/push/subscribe/${encodeURIComponent(subscriptionId)}/dnd`, {
    method: 'PATCH',
    body: JSON.stringify({ do_not_disturb: !!dnd }),
  });
}

/** POST /v1/admin/push/test — fires a test notification to every
 *  device the caller has enrolled.  Returns the count actually
 *  attempted (devices in DnD are skipped server-side).
 */
export async function sendPushTest(api) {
  return api('/v1/admin/push/test', { method: 'POST', body: '{}' });
}

export class ApiError extends Error {
  constructor(status, message) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** Pull the user-facing detail out of an error response.
 *
 *  Backend wraps errors in ``{"error": {"detail": "…"}, "metadata": {…}}``.
 *  Surfacing the whole envelope at the user (request_id, server_time,
 *  circuit_breaker) reads like a system crash — extract just the
 *  detail line, with a sensible HTTP-code fallback when the body
 *  doesn't carry one.
 */
async function _friendlyError(resp) {
  try {
    const body = await resp.json();
    const detail = body?.error?.detail || body?.detail || '';
    if (detail) return detail;
  } catch { /* non-JSON body */ }
  return {
    401: 'Sign in to continue.',
    403: "You don't have permission for that.",
    404: "We couldn't find that.",
    409: 'That conflicts with something that already exists.',
    410: 'That link has expired or already been used.',
    422: "Couldn't process the request — please check your input.",
    429: 'Too many requests — try again in a moment.',
    500: 'Something went wrong on our side. Try again, then ping support if it persists.',
  }[resp.status] || `Something went wrong (HTTP ${resp.status}).`;
}
